import React, { useRef, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import { DragItemTypes, type DragItem, type DropTarget, type DropResult } from './dnd-types';

interface DropZoneProps {
  target: DropTarget;
  onDrop: (item: DragItem, target: DropTarget) => Promise<DropResult> | DropResult | void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  showDropIndicator?: boolean;
  dropIndicatorText?: string;
}

export const DropZone: React.FC<DropZoneProps> = ({
  target,
  onDrop,
  children,
  className = '',
  disabled = false,
  showDropIndicator,
  dropIndicatorText
}) => {
  const shouldShowDropIndicator = showDropIndicator ?? true;
  const dropRef = useRef<HTMLDivElement>(null)
  const [{ isOver, canDrop, dragItem }, drop] = useDrop(() => ({
    accept: target.acceptedTypes,
    drop: async (item: DragItem) => {
      console.log('📦 DROP TRIGGERED!', { item, target, disabled });
      if (disabled) {
        const result: DropResult = { 
          success: false, 
          message: 'Drop target is disabled',
          targetType: target.type,
          targetId: target.id,
          targetName: target.name
        };
        return result;
      }

      try {
        const result = await onDrop(item, target);
        const dropResult: DropResult = result || {
          success: true,
          targetType: target.type,
          targetId: target.id,
          targetName: target.name
        };
        
        console.log('✅ DROP COMPLETED:', dropResult);
        return dropResult;
      } catch (error) {
        console.error('❌ DROP FAILED:', error);
        const errorResult: DropResult = {
          success: false,
          message: error instanceof Error ? error.message : 'Drop operation failed',
          targetType: target.type,
          targetId: target.id,
          targetName: target.name
        };
        return errorResult;
      }
    },
    canDrop: (item: DragItem) => {
      console.log('🤔 CAN DROP CHECK:', { item, target, disabled });
      
      if (disabled) {
        console.log('❌ DROP DISABLED');
        return false;
      }

      // Check if item type is accepted
      if (!target.acceptedTypes.includes(item.type)) {
        console.log('❌ ITEM TYPE NOT ACCEPTED:', item.type, 'accepted:', target.acceptedTypes);
        return false;
      }
      
      // Prevent dropping on self for QUERY items
      if (target.type === 'collection' && item.type === DragItemTypes.QUERY && item.collectionId === target.id) {
        const canDrop = !!item.folderId; // Only allow if query is currently in a folder (moving to collection root)
        console.log('🎯 SELF DROP CHECK (collection):', { canDrop, hasFolderId: !!item.folderId });
        return canDrop;
      }
      
      if (target.type === 'folder' && item.type === DragItemTypes.QUERY && item.folderId === target.id) {
        console.log('❌ SAME FOLDER DROP');
        return false; // Can't drop query on its current folder
      }

      // EDITOR_TAB items can always be dropped to create new queries
      if (item.type === DragItemTypes.EDITOR_TAB) {
        console.log('✅ EDITOR TAB CAN DROP');
        return true;
      }
      
      console.log('✅ CAN DROP: YES');
      return true;
    },
    hover: (item: DragItem) => {
      console.log('👆 HOVER:', target.type, target.id, 'item:', item.type);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      dragItem: monitor.getItem(),
    }),
  }), [target, onDrop, disabled]);

  useEffect(() => {
    drop(dropRef);
  }, [drop]);

  const isActive = isOver && canDrop;
  const isInvalid = isOver && !canDrop;

  let dropIndicatorClass = '';
  let dropIndicatorMessage = '';

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (shouldShowDropIndicator && dragItem !== null) {
    if (isActive) {
      dropIndicatorClass = 'ring-2 ring-green-500 ring-opacity-50 bg-green-50 dark:bg-green-900/20';
      
      // Create contextual drop message based on item type
      if (dragItem.type === DragItemTypes.EDITOR_TAB) {
        const tabName = dragItem.label || 'Untitled Tab';
        const targetName = target.name || target.id;
        dropIndicatorMessage = dropIndicatorText || `Save "${tabName}" to ${target.type} "${targetName}"`;
      } else if (dragItem.type === DragItemTypes.QUERY) {
        const queryName = dragItem.name || 'Untitled Query';
        const targetName = target.name || target.id;
        dropIndicatorMessage = dropIndicatorText || `Move "${queryName}" to ${target.type} "${targetName}"`;
      } else {
        dropIndicatorMessage = dropIndicatorText || `Drop ${dragItem.type} here`;
      }
    } else if (isInvalid) {
      dropIndicatorClass = 'ring-2 ring-red-500 ring-opacity-50 bg-red-50 dark:bg-red-900/20';
      
      // Provide specific feedback for why drop is invalid
      if (dragItem.type === DragItemTypes.QUERY && target.type === 'collection' && dragItem.collectionId === target.id && !dragItem.folderId) {
        dropIndicatorMessage = 'Query is already in this collection';
      } else if (dragItem.type === DragItemTypes.QUERY && target.type === 'folder' && dragItem.folderId === target.id) {
        dropIndicatorMessage = 'Query is already in this folder';
      } else if (!target.acceptedTypes.includes(dragItem.type)) {
        dropIndicatorMessage = `${target.type} doesn't accept ${dragItem.type} items`;
      } else {
        dropIndicatorMessage = 'Cannot drop here';
      }
    } else if (isOver) {
      dropIndicatorClass = 'ring-2 ring-blue-500 ring-opacity-50 bg-blue-50 dark:bg-blue-900/20';
      dropIndicatorMessage = 'Checking...';
    }
  }

  return (
    <div
      ref={dropRef}
      className={`
        relative transition-all duration-200 ease-out
        ${dropIndicatorClass}
        ${className}
      `}
      data-testid={`drop-zone-${target.type}-${target.id}`}
    >
      {children}
      
      {/* Drop Indicator Overlay */}
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
      {shouldShowDropIndicator && dragItem !== null && isOver && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50">
          <div className={`
            px-3 py-2 rounded-lg shadow-lg text-sm font-medium max-w-xs text-center
            ${isActive 
              ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 border border-green-300 dark:border-green-600' 
              : isInvalid 
                ? 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-600'
                : 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 border border-blue-300 dark:border-blue-600'
            }
          `}>
            {dropIndicatorMessage}
          </div>
        </div>
      )}
    </div>
  );
};